import { memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';

import Loading from './Loading';
import { useImageSearch } from 'hooks/useImageSearch';
import useDragAndDrop from 'hooks/useDragAndDrop';
import { useNavigate } from 'react-router';
import { useCadSearch } from 'hooks/useCadSearch';
import { isCadFile } from '@nyris/nyris-api';
import Hint from './Hint';
import { clone } from 'lodash';
import useRequestStore from '../stores/request/requestStore';
import useUiStore from '../stores/ui/uiStore';

interface Props {
  onChangeLoading?: any;
  isLoading?: boolean;
}

function DragDropFile(props: Props) {
  const { isLoading } = props;
  let navigate = useNavigate();

  const { t } = useTranslation();
  const { singleImageSearch } = useImageSearch();
  const { cadSearch } = useCadSearch();

  const preFilterList = useRequestStore(state => state.preFilterList);
  const specifications = useRequestStore(state => state.specifications);
  const setRequestImages = useRequestStore(state => state.setRequestImages);
  const setSpecifications = useRequestStore(state => state.setSpecifications);
  const setNameplateNotificationText = useRequestStore(
    state => state.setNameplateNotificationText,
  );
  const setShowNotMatchedError = useRequestStore(
    state => state.setShowNotMatchedError,
  );
  const setAlgoliaFilter = useRequestStore(state => state.setAlgoliaFilter);
  const setPreFilter = useRequestStore(state => state.setPreFilter);
  const setShowLoading = useRequestStore(state => state.setShowLoading);
  const setNameplateImage = useRequestStore(state => state.setNameplateImage);
  const setIsFindApiLoading = useUiStore(state => state.setIsFindApiLoading);

  const handleUpload = (file: File) => {
    if (isCadFile(file)) {
      cadSearch({
        file: file,
        settings: window.settings,
        newSearch: true,
      }).then(res => {});

      return;
    }

    // Navigate to the results page immediately so the user sees the request
    // image preview and skeleton cards while the search runs, instead of a
    // blocking spinner on the home page.
    navigate('/result');

    singleImageSearch({
      image: file,
      settings: window.settings,
      showFeedback: true,
      clearPostFilter: true,
      newSearch: true,
    }).then(singleImageResp => {
      const specificationPrefilter =
        singleImageResp?.image_analysis?.specification?.prefilter_value || null;
      const hasPrefilter = preFilterList.filter((filter: any) =>
        filter.values.includes(specificationPrefilter),
      );
      if (specificationPrefilter) {
        setRequestImages([]);
        setShowNotMatchedError(false);
        if (hasPrefilter.length) {
          setSpecifications(
            clone(singleImageResp.image_analysis.specification),
          );
          setNameplateImage(file);
          setPreFilter({
            [singleImageResp.image_analysis?.specification?.prefilter_value]:
              true,
          });
          setAlgoliaFilter(
            `${window.settings.alogoliaFilterField}:'${singleImageResp.image_analysis?.specification?.prefilter_value}'`,
          );

          setShowLoading(false);
          navigate('/result');

          setTimeout(() => {
            setNameplateNotificationText(
              t('We have successfully defined the search criteria', {
                prefilter_value: specificationPrefilter,
                preFilterTitle:
                  window.settings.preFilterTitle?.toLocaleLowerCase(),
              }),
            );
          }, 1000);
          setTimeout(() => {
            setNameplateNotificationText('');
          }, 6000);
        }
        if (!hasPrefilter.length && window.settings.preFilterOption) {
          setSpecifications(
            clone({
              ...singleImageResp.image_analysis.specification,
              specificationPrefilter,
            }),
          );
          navigate('/result');
          setPreFilter({});
          setAlgoliaFilter('');
          setShowLoading(false);
          setShowNotMatchedError(true);
          setTimeout(() => {
            setNameplateNotificationText(
              t('Extracted details from the nameplate could not be matched', {
                preFilterTitle:
                  window.settings.preFilterTitle?.toLocaleLowerCase(),
              }),
            );
          }, 1000);
          setTimeout(() => {
            setNameplateNotificationText('');
          }, 6000);
        }
      } else {
        if (!specifications?.is_nameplate) {
          setSpecifications({ ...specifications, is_nameplate: false });
        }
        setShowLoading(false);
        navigate('/result');
      }
    }).catch(() => {
      // A failed image decode/search must not leave the results page stuck
      // on skeleton cards; clear loading so the empty state can render.
      setIsFindApiLoading(false);
      setShowLoading(false);
    });
  };

  const { isDragging, dragProps } = useDragAndDrop({
    onDropCallback: handleUpload,
  });

  return (
    <>
      <label
        htmlFor="select_file"
        className={
          'flex flex-col items-center justify-center mt-8 bg-white drag-n-drop-label'
        }
      >
        {isLoading && <Loading />}

        <div
          className={
            'relative flex flex-col items-center justify-center w-full'
          }
          {...dragProps}
        >
          <div
            className={twMerge([
              'drag-n-drop-inner',
              'flex flex-col items-center w-full cursor-pointer pb-4 pt-4 rounded-[12px]',
              'text-primary',
              'border-2 border-dashed border-[#e0e0e0]',
              isDragging && 'bg-[#F0EFFF]',
            ])}
          >
            <div className="font-bold text-sm" style={{ fontSize: 14 }}>
              {t('Drop or click to upload an image')}
            </div>
            <Hint />
            <input
              onChange={e => {
                e.stopPropagation();
                if (e.target.files && e.target.files[0]) {
                  handleUpload(e.target.files[0]);
                  e.target.value = '';
                }
              }}
              style={{}}
              type="file"
              name="file"
              id="select_file"
              className="absolute z-[-1] opacity-0"
              placeholder="Choose photo"
              accept={
                '.stp,.step,.stl,.obj,.glb,.gltf,.heic,.heif,.pdf,image/*'
              }
            />
          </div>
        </div>
      </label>
    </>
  );
}

export default memo(DragDropFile);
